import { v4 as uuidv4 } from "uuid";

export interface PendingRecording {
  id: string;
  title: string;
  template: string;
  audioBlob: Blob; // For compatibility - reconstructed from audioData on retrieval
  audioData?: ArrayBuffer; // CRITICAL: Store as ArrayBuffer for iOS stability
  timestamp: number;
  duration: number;
  user_id: string;
  status: "pending" | "uploading" | "error";
  attempts: number;
  lastAttempt?: number;
  nextAttempt?: number; // New field for scheduling retries
  meetingId?: string;
  transcriptId?: string; // New field to store AssemblyAI transcript ID for resuming
  transcriptText?: string; // CRITICAL: Cache completed transcript text locally to prevent data loss
  audioSize?: number; // Store audio size for diagnostics
  audioMimeType?: string; // Store MIME type for ArrayBuffer reconstruction
  errorMessage?: string; // Store error message for diagnostics
  transcription_model?: string; // AssemblyAI model: 'universal' (multilingual) or 'best' (English-only)
}

export class OfflineStorageService {
  private DB_NAME = "meetingRecorderDb";
  private DB_VERSION = 3; // Increased version to match user-preferences-service
  private RECORDINGS_STORE = "pendingRecordings";
  private PREFERENCES_STORE = "userPreferences"; // New store from user-preferences-service

  async initDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = request.result;
        const oldVersion = event.oldVersion;

        // Create pendingRecordings store if upgrading from version 0 (fresh install)
        if (
          oldVersion < 1 &&
          !db.objectStoreNames.contains(this.RECORDINGS_STORE)
        ) {
          const store = db.createObjectStore(this.RECORDINGS_STORE, {
            keyPath: "id",
          });
          store.createIndex("user_id", "user_id", { unique: false });
          store.createIndex("status", "status", { unique: false });
          store.createIndex("timestamp", "timestamp", { unique: false });
        }

        // Add nextAttempt index if upgrading from version 1
        if (
          oldVersion < 2 &&
          db.objectStoreNames.contains(this.RECORDINGS_STORE)
        ) {
          const tx = (event.target as IDBOpenDBRequest).transaction;
          if (tx) {
            const store = tx.objectStore(this.RECORDINGS_STORE);
            if (!store.indexNames.contains("nextAttempt")) {
              store.createIndex("nextAttempt", "nextAttempt", {
                unique: false,
              });
            }
          }
        }

        // Create preferences store if upgrading to version 3
        if (
          oldVersion < 3 &&
          !db.objectStoreNames.contains(this.PREFERENCES_STORE)
        ) {
          const preferencesStore = db.createObjectStore(
            this.PREFERENCES_STORE,
            { keyPath: "userId" },
          );
          preferencesStore.createIndex("lastUpdated", "lastUpdated", {
            unique: false,
          });
        }
      };

      request.onsuccess = () => resolve(request.result);
    });
  }

  async saveRecording(recording: PendingRecording): Promise<string> {
    try {
      // Validate blob first
      if (!recording.audioBlob || recording.audioBlob.size === 0) {
        console.error("❌ Invalid blob - cannot save recording");
        throw new Error("Invalid audio blob");
      }

      console.log(
        `📝 Saving recording ${recording.id} (${Math.round(recording.audioBlob.size / 1024 / 1024)}MB)...`,
      );

      // SIMPLIFIED: Store blob directly without ArrayBuffer conversion
      // iOS FileReader is unreliable - accept that blob might be lost on iOS but at least save it
      const enhancedRecording = {
        ...recording,
        audioSize: recording.audioBlob.size,
        audioMimeType: recording.audioBlob.type || "audio/webm",
        // Keep audioBlob as-is - no conversion
      };

      const db = await this.initDatabase();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(
          [this.RECORDINGS_STORE],
          "readwrite",
        );
        const store = transaction.objectStore(this.RECORDINGS_STORE);
        const request = store.put(enhancedRecording);

        request.onsuccess = () => {
          console.log(
            `✅ Saved recording ${recording.id} (blob stored directly)`,
          );
          resolve(recording.id);
        };

        request.onerror = () => {
          console.error("IndexedDB put error:", request.error);
          reject(request.error);
        };

        transaction.oncomplete = () => db.close();

        transaction.onerror = () => {
          console.error("Transaction error:", transaction.error);
          reject(transaction.error);
        };
      });
    } catch (error) {
      console.error("❌ Failed to save recording:", error);
      throw error;
    }
  }

  // Helper method to reconstruct Blob from stored data
  private reconstructBlob(recording: any): PendingRecording {
    // If we have ArrayBuffer data (legacy), reconstruct Blob from it
    if (recording.audioData && recording.audioData instanceof ArrayBuffer) {
      const blob = new Blob([recording.audioData], {
        type: recording.audioMimeType || "audio/webm",
      });

      return {
        ...recording,
        audioBlob: blob,
      };
    }

    // If audioBlob is present (current approach), use it as-is
    if (recording.audioBlob) {
      return recording;
    }

    // No audio data available
    console.warn("⚠️ Recording has no audio data:", recording.id);
    return recording;
  }

  async getRecordings(
    user_id: string,
    status?: "pending" | "uploading" | "error",
  ): Promise<PendingRecording[]> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readonly");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("user_id");
      const request = index.getAll(user_id);

      request.onsuccess = () => {
        let recordings = (request.result as any[]).map((r) =>
          this.reconstructBlob(r),
        );
        if (status) {
          recordings = recordings.filter((r) => r.status === status);
        }

        // If we're looking for pending recordings, filter out those not ready for retry
        if (status === "pending") {
          const now = Date.now();
          recordings = recordings.filter(
            (r) => !r.nextAttempt || r.nextAttempt <= now,
          );
        }

        // Sort by timestamp (most recent first)
        recordings.sort((a, b) => b.timestamp - a.timestamp);

        resolve(recordings);
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  async getReadyRecordings(user_id: string): Promise<PendingRecording[]> {
    // First, recover any recordings stuck in 'uploading' state from a previous crash
    const uploadingResetCount = await this.resetStaleUploading(user_id);
    if (uploadingResetCount > 0) {
      console.log(`[Recovery] Reset ${uploadingResetCount} stale 'uploading' recording(s)`);
    }

    // Also reset pending recordings stuck with far-future retry times (1+ hour in future)
    const pendingResetCount = await this.resetStalePending(user_id);
    if (pendingResetCount > 0) {
      console.log(`[Recovery] Reset ${pendingResetCount} stale 'pending' recording(s) with far-future retries`);
    }

    const db = await this.initDatabase();
    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readonly");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const userIndex = store.index("user_id");
      const request = userIndex.getAll(user_id);

      request.onsuccess = () => {
        const recordings = (request.result as any[])
          .map((r) => this.reconstructBlob(r))
          .filter(
            (r) =>
              r.status === "pending" &&
              (!r.nextAttempt || r.nextAttempt <= now),
          );

        resolve(recordings);
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  /**
   * Resets recordings that have been stuck in 'uploading' status for longer than
   * the given threshold (default: 10 minutes). This handles the case where the
   * app closed or crashed while a recording was mid-upload, leaving it permanently
   * stuck and invisible to the queue processor (which only reads 'pending').
   */
  async resetStaleUploading(
    user_id: string,
    staleThresholdMs = 10 * 60 * 1000,
  ): Promise<number> {
    const db = await this.initDatabase();
    const now = Date.now();
    const staleBeforeTimestamp = now - staleThresholdMs;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("user_id");
      const request = index.getAll(user_id);
      let resetCount = 0;
      let putCount = 0;
      let completedPuts = 0;

      request.onsuccess = () => {
        const recordings = request.result as any[];
        const staleUploading = recordings.filter(
          (r) =>
            r.status === "uploading" &&
            // Either it has a lastAttempt that's older than the threshold,
            // or it has no lastAttempt (stale from an unknown time)
            (!r.lastAttempt || r.lastAttempt < staleBeforeTimestamp),
        );

        putCount = staleUploading.length;

        if (putCount === 0) {
          // No stale recordings to reset
          resolve(0);
          return;
        }

        for (const recording of staleUploading) {
          console.log(
            `🔄 [RECOVERY] Resetting stale 'uploading' recording to 'pending': ${recording.id} (last attempt: ${recording.lastAttempt ? new Date(recording.lastAttempt).toISOString() : "unknown"})`,
          );
          recording.status = "pending";
          recording.nextAttempt = undefined; // Retry immediately
          const putRequest = store.put(recording);

          putRequest.onsuccess = () => {
            completedPuts++;
            resetCount++;
            // Resolve when all puts complete
            if (completedPuts === putCount) {
              resolve(resetCount);
            }
          };

          putRequest.onerror = () => {
            console.error(`Failed to reset recording ${recording.id}:`, putRequest.error);
            completedPuts++;
            // Continue with other recordings even if one fails
            if (completedPuts === putCount) {
              resolve(resetCount);
            }
          };
        }
      };

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  }

  /**
   * Resets recordings that are stuck in 'pending' status with far-future retry times.
   * On mobile, retries can get scheduled for hours/days ahead if errors keep occurring.
   * This ensures recordings don't get permanently locked waiting for a future retry.
   * Threshold: if nextAttempt is more than 1 hour in the future, reset it to retry now.
   */
  async resetStalePending(
    user_id: string,
    staleThresholdMs = 60 * 60 * 1000, // 1 hour
  ): Promise<number> {
    const db = await this.initDatabase();
    const now = Date.now();
    const resetBeforeTimestamp = now + staleThresholdMs; // If nextAttempt is MORE than 1 hour away

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("user_id");
      const request = index.getAll(user_id);
      let resetCount = 0;
      let putCount = 0;
      let completedPuts = 0;

      request.onsuccess = () => {
        const recordings = request.result as any[];
        const stalePending = recordings.filter(
          (r) =>
            r.status === "pending" &&
            r.nextAttempt &&
            r.nextAttempt > resetBeforeTimestamp, // Scheduled too far in future
        );

        putCount = stalePending.length;

        if (putCount === 0) {
          // No stale pending recordings to reset
          resolve(0);
          return;
        }

        for (const recording of stalePending) {
          console.log(
            `🔄 [RECOVERY] Resetting stale 'pending' recording with far-future retry: ${recording.id} (was scheduled for ${new Date(recording.nextAttempt).toISOString()})`,
          );
          recording.nextAttempt = undefined; // Retry immediately
          const putRequest = store.put(recording);

          putRequest.onsuccess = () => {
            completedPuts++;
            resetCount++;
            // Resolve when all puts complete
            if (completedPuts === putCount) {
              resolve(resetCount);
            }
          };

          putRequest.onerror = () => {
            console.error(`Failed to reset pending recording ${recording.id}:`, putRequest.error);
            completedPuts++;
            // Continue with other recordings even if one fails
            if (completedPuts === putCount) {
              resolve(resetCount);
            }
          };
        }
      };

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  }

  /**
   * Returns ALL recordings regardless of their nextAttempt value, including those
   * in 'pending' status waiting for a future retry. Used by diagnostics views.
   */
  async getAllRecordingsIncludingDeferred(
    user_id: string,
  ): Promise<PendingRecording[]> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readonly");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("user_id");
      const request = index.getAll(user_id);

      request.onsuccess = () => {
        const recordings = (request.result as any[]).map((r) =>
          this.reconstructBlob(r),
        );
        recordings.sort((a, b) => b.timestamp - a.timestamp);
        resolve(recordings);
      };

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  }

  async getRecording(id: string): Promise<PendingRecording | null> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readonly");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const request = store.get(id);

      request.onsuccess = () => {
        const recording = request.result;
        resolve(recording ? this.reconstructBlob(recording) : null);
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  async removeRecording(id: string): Promise<void> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  // Clear all recordings for a user (useful for cleanup)
  async clearAllRecordings(user_id: string): Promise<number> {
    const recordings = await this.getRecordings(user_id);
    console.log(
      `🗑️ Clearing ${recordings.length} recordings for user ${user_id}`,
    );

    for (const recording of recordings) {
      await this.removeRecording(recording.id);
    }

    console.log(`✅ Cleared ${recordings.length} recordings`);
    return recordings.length;
  }

  async updateRecordingStatus(
    id: string,
    status: "pending" | "uploading" | "error",
    attempts?: number,
  ): Promise<void> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const request = store.get(id);

      request.onsuccess = async () => {
        const recording = request.result;
        if (recording) {
          recording.status = status;
          if (attempts !== undefined) recording.attempts = attempts;
          recording.lastAttempt = Date.now();
          const updateRequest = store.put(recording);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error("Recording not found"));
        }
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  // Enhanced update method with retry scheduling
  async updateRecordingRetry(
    id: string,
    status: "pending" | "uploading" | "error",
    attempts: number,
    nextAttempt?: number,
  ): Promise<void> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const request = store.get(id);

      request.onsuccess = async () => {
        const recording = request.result;
        if (recording) {
          recording.status = status;
          recording.attempts = attempts;
          recording.lastAttempt = Date.now();
          if (nextAttempt) {
            recording.nextAttempt = nextAttempt;
          }

          const updateRequest = store.put(recording);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error("Recording not found"));
        }
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  // Add method to store transcript ID for resumable transcription
  async updateRecordingWithTranscriptId(
    id: string,
    transcriptId: string | null,
    status: "pending" | "uploading" | "error" = "pending",
    attempts: number = 1,
    nextAttempt?: number,
  ): Promise<void> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const request = store.get(id);

      request.onsuccess = async () => {
        const recording = request.result;
        if (recording) {
          recording.transcriptId = transcriptId;
          recording.status = status;
          recording.attempts = attempts;
          recording.lastAttempt = Date.now();
          if (nextAttempt) {
            recording.nextAttempt = nextAttempt;
          }

          const updateRequest = store.put(recording);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error("Recording not found"));
        }
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  // CRITICAL: Method to cache completed transcript text locally
  // This provides a backup if the database write fails or connection is lost
  async updateRecordingWithTranscript(
    id: string,
    transcriptText: string,
    status: "pending" | "uploading" | "error" = "uploading",
  ): Promise<void> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const request = store.get(id);

      request.onsuccess = async () => {
        const recording = request.result;
        if (recording) {
          recording.transcriptText = transcriptText;
          recording.status = status;
          recording.lastAttempt = Date.now();

          const updateRequest = store.put(recording);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error("Recording not found"));
        }
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  async clearOldRecordings(maxAgeDays = 7): Promise<void> {
    try {
      const db = await this.initDatabase();
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("timestamp");
      const now = Date.now();
      const cutoff = now - maxAgeDays * 24 * 60 * 60 * 1000;

      const request = index.openCursor();

      request.onsuccess = (event) => {
        const cursor = request.result;
        if (cursor) {
          const record = cursor.value;
          if (record.timestamp < cutoff) {
            store.delete(record.id);
          }
          cursor.continue();
        }
      };

      transaction.oncomplete = () => db.close();
    } catch (error) {
      console.error("Error clearing old recordings:", error);
    }
  }

  // Get counts of recordings by status for monitoring
  async getRecordingStats(user_id: string): Promise<{
    pending: number;
    uploading: number;
    error: number;
    totalSize: number;
  }> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readonly");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("user_id");
      const request = index.getAll(user_id);

      request.onsuccess = () => {
        const recordings = request.result as PendingRecording[];
        const stats = {
          pending: recordings.filter((r) => r.status === "pending").length,
          uploading: recordings.filter((r) => r.status === "uploading").length,
          error: recordings.filter((r) => r.status === "error").length,
          totalSize: recordings.reduce(
            (acc, rec) => acc + (rec.audioSize || 0),
            0,
          ),
        };

        resolve(stats);
      };

      request.onerror = () => reject(request.error);

      transaction.oncomplete = () => db.close();
    });
  }

  // Reset all errored recordings back to pending so they can be retried
  async resetErroredRecordings(user_id: string): Promise<number> {
    const db = await this.initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.RECORDINGS_STORE], "readwrite");
      const store = transaction.objectStore(this.RECORDINGS_STORE);
      const index = store.index("user_id");
      const request = index.getAll(user_id);
      let resetCount = 0;

      request.onsuccess = () => {
        const recordings = request.result as any[];
        const errored = recordings.filter((r) => r.status === "error");

        for (const recording of errored) {
          recording.status = "pending";
          recording.attempts = 0;
          recording.nextAttempt = undefined;
          recording.lastAttempt = undefined;
          store.put(recording);
          resetCount++;
        }

        resolve(resetCount);
      };

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  }
}

export const offlineStorage = new OfflineStorageService();
